import { useCallback, useRef, useState, type ReactNode } from "react";

interface SplitPanelProps {
  left: ReactNode;
  right: ReactNode;
  defaultSplit?: number;
  minWidth?: number;
}

export function SplitPanel({
  left,
  right,
  defaultSplit = 50,
  minWidth = 300,
}: SplitPanelProps) {
  const [split, setSplit] = useState(defaultSplit);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handlePointerDown = useCallback(() => {
    isDragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = (x / rect.width) * 100;
      const minPct = (minWidth / rect.width) * 100;
      const maxPct = 100 - minPct;
      setSplit(Math.min(maxPct, Math.max(minPct, pct)));
    },
    [minWidth],
  );

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full overflow-hidden"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      <div className="h-full overflow-hidden" style={{ width: `${split}%` }}>
        {left}
      </div>
      <div
        className="h-full w-1 shrink-0 cursor-col-resize bg-border-subtle transition-colors hover:bg-accent active:bg-accent"
        onPointerDown={handlePointerDown}
      />
      <div
        className="h-full overflow-hidden"
        style={{ width: `${100 - split}%` }}
      >
        {right}
      </div>
    </div>
  );
}
